/**
 * ----------------------------------------------------------------------------------------------------------------------
 * EMAIL SIGNUP FORM (SAILTHRU)
 * ----------------------------------------------------------------------------------------------------------------------
 */

const RECAPTCHA_CALLBACK = 'themeEmailSignupRecaptchaLoaded';
const RECAPTCHA_SRC = `https://www.google.com/recaptcha/api.js?onload=${RECAPTCHA_CALLBACK}&render=explicit`;
const SUBMIT_FALLBACK_DELAY = 8000;

const pendingForms = new Set();

let scriptRequested = false;

const isRecaptchaReady = () => typeof window.grecaptcha?.render === 'function';

window[RECAPTCHA_CALLBACK] = () => {
  pendingForms.forEach((form) => form.renderRecaptcha());
  pendingForms.clear();
};

const loadRecaptchaScript = () => {
  if (scriptRequested) return;

  scriptRequested = true;

  const script = document.createElement('script');

  script.src = RECAPTCHA_SRC;
  script.async = true;
  script.defer = true;

  document.head.appendChild(script);
};

class EmailSignupForm extends HTMLElement {
  #widgetId = null;
  #cleared = false;
  #executePending = false;
  #fallbackTimer = null;

  connectedCallback() {
    this.form = this.querySelector('form');

    if (!this.form) return;

    this.siteKey = this.getAttribute('data-sitekey');
    this.emailInput = this.form.querySelector('input[type="email"]');

    this.form.addEventListener('submit', this.#onSubmit);

    this.#stampConsentDate();

    if (this.siteKey && this.emailInput) {
      this.emailInput.addEventListener('focus', this.#prepareRecaptcha, { once: true });
    }
  }

  disconnectedCallback() {
    pendingForms.delete(this);
    clearTimeout(this.#fallbackTimer);

    this.form?.removeEventListener('submit', this.#onSubmit);
    this.emailInput?.removeEventListener('focus', this.#prepareRecaptcha);
  }

  renderRecaptcha = () => {
    if (this.#widgetId !== null || !this.siteKey || !isRecaptchaReady()) return;

    const container = this.form.querySelector('[data-recaptcha-container]');

    if (!container) return;

    this.#widgetId = window.grecaptcha.render(container, {
      sitekey: this.siteKey,
      size: 'invisible',
      badge: 'bottomright',
      callback: this.#onToken,
    });

    if (this.#executePending) {
      this.#executePending = false;
      window.grecaptcha.execute(this.#widgetId);
    }
  };

  #stampConsentDate = () => {
    const field = this.form.querySelector('.casl_date_v1');

    if (!field) return;

    field.value = new Date().toISOString().replace(/-/g, '').split('T')[0];
  };

  #prepareRecaptcha = () => {
    if (isRecaptchaReady()) {
      this.renderRecaptcha();
      return;
    }

    pendingForms.add(this);
    loadRecaptchaScript();
  };

  #onToken = () => {
    this.#submit();
  };

  #submit = () => {
    if (this.#cleared) return;

    this.#cleared = true;
    clearTimeout(this.#fallbackTimer);
    this.form.submit();
  };

  #onSubmit = (event) => {
    this.#stampConsentDate();

    if (!this.form.checkValidity()) return;

    if (!this.siteKey || this.#cleared) return;

    event.preventDefault();

    clearTimeout(this.#fallbackTimer);
    this.#fallbackTimer = setTimeout(this.#submit, SUBMIT_FALLBACK_DELAY);

    if (this.#widgetId === null) {
      this.#executePending = true;
      this.#prepareRecaptcha();
      return;
    }

    window.grecaptcha.execute(this.#widgetId);
  };
}

window.customElements.define('email-signup-form', EmailSignupForm);
